"use client";

import { useRouter } from "next/navigation";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	getAttributionSummary,
	getMetaCustomData,
	trackConversionWithJourney,
} from "@/lib/journey-tracker";

interface User {
	id: string;
	email: string;
	name: string;
	role: "super_admin" | "admin" | "user" | "contractor:blog";
	is_active: boolean;
	created_at: string;
}

interface UserAuthState {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

interface UserAuthContextType extends UserAuthState {
	login: (
		email: string,
		password: string,
	) => Promise<{ success: boolean; error?: string; redirectPath?: string }>;
	signup: (
		name: string,
		email: string,
		password: string,
	) => Promise<{ success: boolean; error?: string }>;
	logout: () => Promise<void>;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(
	undefined,
);

export function UserAuthProvider({ children }: { children: React.ReactNode }) {
	const router = useRouter();
	const [user, setUser] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Fetch current user on mount
	const fetchUser = useCallback(async () => {
		try {
			const response = await fetch("/api/admin/auth/me?probe=1", {
				credentials: "include",
			});
			if (response.ok) {
				if (response.status === 204) {
					setUser(null);
					return;
				}

				const data = await response.json();
				// Accept all authenticated users
				if (data.user) {
					setUser(data.user);
				} else {
					setUser(null);
				}
			} else {
				// 401 is expected when not logged in - don't treat as error
				setUser(null);
			}
		} catch {
			// Network errors are silent - user just isn't logged in
			setUser(null);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	// Login function
	const login = useCallback(
		async (
			email: string,
			password: string,
		): Promise<{ success: boolean; error?: string; redirectPath?: string }> => {
			try {
				const response = await fetch("/api/auth/login", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ email, password }),
				});

				if (!response.ok) {
					const errData = await response.json().catch(() => null);
					return { success: false, error: errData?.error || "Login failed" };
				}

				const data = await response.json();
				setUser(data.user);
				return {
					success: true,
					redirectPath: data.redirectPath || "/dashboard",
				};
			} catch (error) {
				console.error("Login error:", error);
				return { success: false, error: "An unexpected error occurred" };
			}
		},
		[],
	);

	// Signup function
	const signup = useCallback(
		async (
			name: string,
			email: string,
			password: string,
		): Promise<{ success: boolean; error?: string }> => {
			try {
				// Get journey/attribution data
				const attribution = getAttributionSummary();
				const metaCustomData = getMetaCustomData();

				const response = await fetch("/api/auth/signup", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						name,
						email,
						password,
						attribution,
						metaCustomData,
					}),
				});

				if (!response.ok) {
					const errData = await response.json().catch(() => null);
					return { success: false, error: errData?.error || "Signup failed" };
				}

				const data = await response.json();
				setUser(data.user);

				// Track signup with journey context in Umami
				trackConversionWithJourney("signup", { email });

				return { success: true };
			} catch (error) {
				console.error("Signup error:", error);
				return { success: false, error: "An unexpected error occurred" };
			}
		},
		[],
	);

	// Logout function
	const logout = useCallback(async (): Promise<void> => {
		try {
			await fetch("/api/admin/auth/logout", { method: "POST" });
		} catch (error) {
			console.error("Logout error:", error);
		} finally {
			setUser(null);
			router.push("/auth/login");
		}
	}, [router]);

	const value: UserAuthContextType = useMemo(
		() => ({
			user,
			isAuthenticated: !!user,
			isLoading,
			login,
			signup,
			logout,
		}),
		[user, isLoading, login, signup, logout],
	);

	return (
		<UserAuthContext.Provider value={value}>
			{children}
		</UserAuthContext.Provider>
	);
}

export function useUserAuth(): UserAuthContextType {
	const context = useContext(UserAuthContext);
	if (context === undefined) {
		throw new Error("useUserAuth must be used within a UserAuthProvider");
	}
	return context;
}
